import { ReactNode } from 'react';
import LikeIcon from './icon/LikeIcon';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { axios } from '#api/axios';
import { useNavigate, useParams } from 'react-router';
import useThreadLikeMutations from 'src/r_page/Trainer-Community/useThreadLikeMutations';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { ShareIcon } from 'src/r_page/main/icon/ShareIcon';
import { useShareTharedUrl } from 'src/refactor_shared/hooks/useShareThreadUrl';
import { usePostQnaLikesMutaiton } from '#api/community';
import { isUser } from 'src/refactor_shared/util/isUser';

const QuestionComment = ({ children }: { children: ReactNode }) => {
  const { id } = useParams();

  const { data: answerCount } = useQuery({
    queryKey: ['get-qna-community-thread-comment-count', id],
    queryFn: async () => await axios.get(`/consumer/qna/${id}/answers-count`),
    enabled: !!id,
    select: ({ data }) => data
  });

  return (
    <div className="p-20">
      <div className="flex flex-col gap-63">
        <div className="flex gap-4 pb-16">
          <h1 className="font-bold text-[18px] leading-24 text-[#EAE8ED]">
            전문가 답변
          </h1>
          <span className="text-[16px] font-medium text-[#9170F2]">
            {answerCount ? answerCount : 0}
          </span>
        </div>
        {answerCount === 0 && (
          <span className="text-center text-[#5C576B] text-[14px] leading-20">
            아직 작성된 답변이 없어요.
          </span>
        )}
      </div>
      <div className="flex flex-col gap-40">{children}</div>
    </div>
  );
};

const Wrap = ({ children }: { children: ReactNode }) => {
  return <div className="flex flex-col gap-8 text-[#C9C7D1]">{children}</div>;
};

const TrainerProfile = ({
  trainerName,
  trainerId,
  trainerProfile,
  time,
  trainerIntro
}: {
  trainerName?: string;
  trainerId: string;
  trainerProfile?: string;
  time?: string;
  trainerIntro: string | null;
}) => {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-16 bg-[#1F1E25] rounded-16 py-16 px-20 text-[#C9C7D1]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-9">
          {trainerProfile ? (
            <img
              onClick={() => navigate(`/trainer/${trainerId}`)}
              src={trainerProfile ? trainerProfile : ''}
              className="object-cover w-48 h-48 rounded-full bg-[#383441] cursor-pointer"
              alt=""
            />
          ) : (
            <div className="bg-[#383441] w-48 h-48 rounded-full" />
          )}

          <div className="flex flex-col gap-4">
            <p
              className="font-semibold text-[14px] text-[#EAE8ED]"
              onClick={() => navigate(`/trainer/${trainerId}`)}
            >
              {trainerName} 트레이너
            </p>
            <p className="text-[#5C576B] text-[10px]">
              {dayjs(time).format('YYYY-MM-DD')}
            </p>
          </div>
        </div>
        <Link
          to={`/trainer/${trainerId}`}
          className="bg-[#7750E9] rounded-8 text-center text-[#EAE8ED] py-8 px-16 text-[14px]"
        >
          문의하기
        </Link>
      </div>
      {trainerIntro ? (
        <p className="w-full text-center text-[14px]">“{trainerIntro}”</p>
      ) : null}
    </div>
  );
};

const Content = ({
  id,
  content,
  likes
}: {
  id?: string;
  content?: string;
  likes: number;
}) => {
  const queryClinet = useQueryClient();
  const { likeMutate } = useThreadLikeMutations('consumer/qna/likes', false);

  return (
    <div className="flex flex-col gap-16  rounded-16 py-16 pb-8 px-20 text-[#C9C7D1]">
      <p
        className="text-[#EAE8ED]"
        dangerouslySetInnerHTML={{
          __html: content ? content : ''
        }}
      />
    </div>
  );
};

const QuestionBarShareAndLike = ({
  targetId,
  isLiked,
  postId
}: {
  targetId?: string;
  isLiked: boolean;
  postId?: string;
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const copyThreadUrl = useShareTharedUrl();
  const postQnaLikesMutation = usePostQnaLikesMutaiton();
  return (
    <div className="flex justify-between px-20 py-8 text-[14px] text-[#8E899F]">
      <button
        className="flex items-center gap-4"
        onClick={() =>
          copyThreadUrl(
            `https://fibud.kr/community/qna/${targetId}`,
            '질문 답변 URL이 복사되었습니다.'
          )
        }
      >
        <ShareIcon /> 공유하기
      </button>
      <button
        className={`flex items-center gap-4 ${
          isLiked ? 'text-[#9170F2]' : 'text-[#8E899F]'
        }`}
        onClick={() => {
          if (isUser()) {
            postQnaLikesMutation.mutate(
              { kind: 'ANSWER', targetId: targetId || '' },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries([
                    'get-qna-community-question-answer',
                    postId
                  ]);
                }
              }
            );
          } else {
            navigate('/sign');
          }
        }}
      >
        <LikeIcon isLiked={isLiked} /> 도움 됐어요
      </button>
    </div>
  );
};

QuestionComment.Wrap = Wrap;
QuestionComment.Content = Content;
QuestionComment.Profile = TrainerProfile;
QuestionComment.QuestionBar = QuestionBarShareAndLike;

export default QuestionComment;
